import type { ModuleSnapshot } from '@ctcells/contract'
import { enumLabel, t } from '@ctcells/i18n'

import { StatusBadge } from '../../atoms/StatusBadge.tsx'
import { staleValue } from '../format.ts'

import { figureMetricTiles, type FigureMetricTile } from './figureMetricsModel.ts'
import type { MetricSpec } from './metricSpecs.ts'
import { moduleSummaryState, type ModuleSummaryChip } from './moduleSummaryState.ts'

/**
 * 원심분리기 «영상 아래» 메트릭 줄 — 장비별 보기 상세 카드의 비전 칸 바로 밑(eric 2026-10-06 「원심분리기 하단에.. 메트릭값 보이게」 →
 * 같은 날 「현재 메트릭을... 원심분리기.. 영상 아래에다가 넣어줘야지.」). 비전 칸이 자료 없이도 늘 서듯 이 줄도 늘 선다.
 *
 * ```
 * ┌──────────────────────────────────────────┐
 * │ 3축 로봇                                  │
 * │ 엔드 이펙터: 그리퍼 (RGD-35-30)            │
 * │ X 0 mm   Y 0 mm   Z 10 mm   그리퍼 파지     │
 * │ 원심분리기                                │
 * │ 속도 ▨ 미수신   RCF ▨ 미수신   온도 ▨ 미수신 │
 * └──────────────────────────────────────────┘
 * ```
 *
 * 🔴 숫자는 핵심 수치 단(`--text-xl`) · 단위는 보조 글자(`--text-sm`)다. 「미수신」은 빗금 표지와 글자가 같이 말한다(색만으로 가르지 않는다).
 * 🔴 낡은 스냅샷(`stale`)이면 제목이 「마지막 메트릭」 — 마지막 값을 지금 값으로 보이지 않는다.
 * 🔴 놓는 자리는 `EquipmentSheet` 가 상세 카드 `lead`(영상 다음)로 넘긴다. ⚠ 오전의 「로터 아래 · 오른쪽」 배치는 걷었다 — 되살리지 마라.
 */
export function FigureMetrics({
  name,
  snapshot,
  spec,
  compactState = true,
}: {
  /** 장비 이름(접근 이름 「원심분리기 메트릭」 · 본체 묶음 이름) */
  name: string
  snapshot: ModuleSnapshot | undefined
  spec: readonly MetricSpec[]
  /** 모듈 앱 · 통합 콘솔 모두 정상 상태만 두 값으로 요약한다. */
  compactState?: boolean
}) {
  /* 🔴 모사 값도 콘솔 · 모듈 앱이 같은 값을 보인다 — 「—」로 접지 않고 표지도 달지 않는다(eric 2026-10-12 「위치는 모사값 빼고」 · 2026-10-26) */
  const tiles = figureMetricTiles(snapshot, spec)
  if (tiles.length === 0) return null
  const stale = snapshot?.stale === true
  /*
   * 🔴 묶음 차례(eric 2026-10-12 「3축 로봇을 위에 올리고.. 밑에.. 박스로 메트릭.. 그 아래에.. 원심분리기 혹은... 콜드 랙 이런 거 적어줘」) —
   *    ① 「3축 로봇」 이름 · 「엔드 이펙터: 그리퍼 (RGD-35-30)」 · 칸 ② 장비 본체(이름 = 장비 이름) ③ 시약 보관(이름 = 소모품 판의 cold rack).
   *    「현재 메트릭」 제목 · 「위치는 모사값」 표지 · 「서버가 아직 보내지 않음」 사유 줄은 걷었다(같은 날) — 빈 값은 칸의 「미수신」이 말한다
   */
  /*
   * ⚠ 「3축 로봇」 머리 아래 「엔드 이펙터: 그리퍼 (RGD-35-30)」 줄(eric 2026-10-12)은 걷었다 — 그리퍼 · 피펫 팁 칸이 같은 것을 말한다
   *   (eric 2026-10-18 「엔드 이펙터: 그리퍼 이거 없애줘. 어짜피 그리퍼 상태로 알 수 잇는 내용이야」 · 모듈 앱 · 통합 콘솔 둘 다). 6축 카드(`RobotDetail`)는 그대로다
   */
  const groups: {
    id: string
    name: string
    tiles: FigureMetricTile[]
  }[] = [
    {
      id: 'gantry',
      name: t('monitoring.eqsheet.gantry'),
      tiles: tiles.filter((tile) => tile.gantry === true),
    },
    {
      id: 'device',
      name,
      tiles: tiles.filter((tile) => tile.gantry !== true && tile.storage !== true),
    },
    {
      id: 'storage',
      name: t('monitoring.supply.chilledRack'),
      tiles: tiles.filter((tile) => tile.storage === true),
    },
  ]
  /*
   * 모듈 상태 칩(「대기」 · 「실행 중」 …) — 맨 위 카드 제목 오른쪽. 운송 로봇 카드(`RobotDetailTiles`)와 같은 자리 · 같은 칩
   * (eric 2026-10-15 「대기 이런 거 맞춰줘」). 정상 상태는 모듈 앱처럼 요약하고 이상 상태는 유지한다. 낡았으면 «마지막 값» 표지가 붙는다
   */
  const state: ModuleSummaryChip | undefined = compactState
    ? moduleSummaryState(snapshot)
    : snapshot === undefined
      ? undefined
      : {
          label: staleValue(enumLabel('enum.moduleState', snapshot.state), stale),
          status: snapshot.state,
        }
  const stateChip =
    state === undefined ? undefined : (
      <StatusBadge label={state.label} originalValue={state.originalValue} status={state.status} />
    )
  return (
    <section aria-label={t('monitoring.eqsheet.metrics', { name })} className="eqmetrics">
      {stale ? <div className="eqmetrics__title">{t('panel.metricsStale')}</div> : null}
      {groups
        .filter((group) => group.tiles.length > 0)
        .map((group, index) => (
          <div className="eqmetrics__group" data-group={group.id} key={group.id}>
            <div className="eqmetrics__grouphead">
              <span className="eqmetrics__groupname">{group.name}</span>
              {/* 장비 본체 카드에도 같은 칩(eric 2026-10-18 「원심분리기 오른쪽에는 칩이 없잖아… 콘솔쪽 원래 장비 화면에도… 공통되게」) */}
              {(index === 0 || group.id === 'device') && stateChip !== undefined ? (
                <span className="eqmetrics__state">{stateChip}</span>
              ) : null}
            </div>
            <MetricTiles tiles={group.tiles} />
          </div>
        ))}
    </section>
  )
}

function MetricTiles({ tiles }: { tiles: readonly FigureMetricTile[] }) {
  return (
    <dl className="eqmetrics__tiles">
      {tiles.map((tile) => (
        <div className="eqmetric" data-metric={tile.key} key={tile.key}>
          <dt className="eqmetric__label">{tile.label}</dt>
          <dd className={`eqmetric__value${tile.value === null ? ' eqmetric__value--empty' : ''}`}>
            {tile.value === null ? (
              <span className="eqtile__nv" title={t('panel.unreceived')}>
                {/* 화면에는 대시 하나(eric 2026-10-19 「미수신이면.. - 이렇게.. 대시로」) · 보조기술은 「미수신」을 읽는다 */}
                <span aria-hidden="true">–</span>
                <span className="a-sr">{t('panel.unreceived')}</span>
              </span>
            ) : (
              <>
                <span className="eqmetric__num">{tile.value}</span>
                {tile.unit === undefined ? null : (
                  <span className="eqmetric__unit">{tile.unit}</span>
                )}
              </>
            )}
          </dd>
        </div>
      ))}
    </dl>
  )
}
